import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { 
  Navigation, 
  Database, 
  TreeDeciduous, 
  AlertCircle, 
  GitCompare, 
  ChevronDown,
  Target,
  Star,
  Users,
  BarChart,
  LineChart,
  Sparkles,
  Building2,
  Upload,
  Car
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface SidebarNavProps extends React.HTMLAttributes<HTMLElement> {}

export function SidebarNav({ className, ...props }: SidebarNavProps) {
  const [isOpen, setIsOpen] = useState(true);
  const { session } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const { data: subscription } = useQuery({
    queryKey: ["subscription", session?.user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', session?.user?.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!session?.user?.id,
  });

  const isTreePlan = subscription?.tier === "tester_tree";

  const mainItems = [
    {
      title: "Discover",
      href: "/",
      icon: Navigation
    },
    {
      title: "Vehicle Manager",
      href: "/vehicle-manager",
      icon: Car,
      subItems: [
        {
          title: "Manufacturer Overview",
          href: "/manufacturer-overview",
          icon: Building2
        },
        {
          title: "Manufacturer Ranking",
          href: "/manufacturer-ranking",
          icon: Star
        },
        {
          title: "Model Overview",
          href: "/model-overview",
          icon: Car
        }
      ]
    },
    {
      title: "Data Request",
      href: "/data-request",
      icon: Upload
    },
    {
      title: "Data Explorer",
      href: "/explorer",
      icon: Database
    },
    {
      title: "Price Intelligence",
      href: "/price-intelligence",
      icon: Target
    },
    {
      title: "Rating Intelligence",
      href: "/rating-intelligence",
      icon: Star
    },
    {
      title: "Brand Monitor",
      href: "/brand-monitor",
      icon: Building2
    },
    {
      title: "Competitor Monitoring",
      href: "/competitor-monitoring",
      icon: Users
    },
    {
      title: "Assortment Analytics",
      href: "/assortment-analytics",
      icon: BarChart
    },
    {
      title: "Market Analytics",
      href: "/market-analytics",
      icon: LineChart
    }
  ];

  const treeItems = [
    {
      title: "Tree Investigation",
      href: "/tree",
      icon: TreeDeciduous
    },
    {
      title: "Missing Content",
      href: "/missing-content",
      icon: AlertCircle
    },
    {
      title: "Tree Changes",
      href: "/tree-changes",
      icon: GitCompare
    }
  ];

  const handleNavigation = (href: string) => {
    if (isTreePlan && !href.includes('tree') && !href.includes('missing-content')) {
      toast({
        variant: "destructive",
        title: "Access Restricted",
        description: "Your current subscription only allows access to tree management features.",
      });
      navigate('/tree');
      return;
    }
    navigate(href);
  };

  return (
    <nav
      className={cn(
        "flex flex-col space-y-1",
        className
      )}
      {...props}
    >
      <h2 className="px-4 py-2 text-lg font-semibold">Navigation</h2>
      {mainItems.map((item) => (
        <div key={item.href}>
          <button
            onClick={() => handleNavigation(item.href)}
            className={cn(
              buttonVariants({ variant: "ghost" }),
              "justify-start w-full",
              isTreePlan && "opacity-50 cursor-not-allowed"
            )}
            disabled={isTreePlan}
          >
            <item.icon className="mr-2 h-4 w-4" />
            {item.title}
          </button>
          {item.subItems && item.subItems.length > 0 && (
            <div className="ml-8">
              {item.subItems.map((sub) => (
                <button
                  key={sub.href}
                  onClick={() => handleNavigation(sub.href)}
                  className={cn(
                    buttonVariants({ variant: "ghost" }),
                    "justify-start w-full text-left py-1 text-sm hover:bg-muted"
                  )}
                  style={{ minHeight: 30 }}
                >
                  <sub.icon className="mr-2 h-4 w-4" />
                  {sub.title}
                </button>
              ))}
            </div>
          )}
          <Separator className="my-1" />
        </div>
      ))}
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "justify-between w-full group"
          )}
        >
          <div className="flex items-center">
            <TreeDeciduous className="mr-2 h-4 w-4" />
            Tree Management
            <sup className="ml-1 text-xs font-medium text-muted-foreground">
              <Sparkles className="h-4 w-4 text-blue-500 inline-block mr-1" />
              beta
            </sup>
          </div>
          <ChevronDown className={cn(
            "h-4 w-4 transition-transform duration-200",
            isOpen ? "transform rotate-180" : ""
          )} />
        </CollapsibleTrigger>
        <CollapsibleContent className="pl-4">
          {treeItems.map((item) => (
            <button
              key={item.href}
              onClick={() => handleNavigation(item.href)}
              className={cn(
                buttonVariants({ variant: "ghost" }),
                "justify-start w-full"
              )}
            >
              <item.icon className="mr-2 h-4 w-4" />
              {item.title}
            </button>
          ))}
        </CollapsibleContent>
      </Collapsible>
    </nav>
  );
}
